/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
// [ION CTC ADMIN PACKAGE TODO]: IMPORTANT: This module is deprecated and needs
// to be replaced with the shared `AdminContainer` component from the applications
// package.
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import Add from '@material-ui/icons/Add';
import classnames from 'classnames';
import {isPreferenceAvailable} from '@ion/applications/src/helpers';
import {getConstrainedGridStyles} from '@ion/applications/src/pages/admin/AdminContainer';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {globalGroupSelector} from '@ion/applications/src/store/selectors/global-group';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {ColumnChooserButton} from '@ion/dls/src/components/buttons/ColumnChooserButton';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {PopupPrimaryButton} from '@ion/dls/src/components/buttons/PopupPrimaryButton';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {RefreshGridButton} from '@ion/dls/src/components/buttons/RefreshGridButton';
import {SaveIconButton} from '@ion/dls/src/components/buttons/SaveIconButton';
import {ViewSubGroupsSwitch} from '@ion/dls/src/components/switch/ViewSubGroupsSwitch';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {EmailExportRequestDialog} from '@ion/dls/src/export/EmailExportRequestDialog';
import {ViewOnlyThisAccountProps} from '@ion/dls/src/grid/IonGrid';
import {useHasGridChanged} from '@ion/dls/src/grid/useHasGridChanged';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {
  DLSTheme,
  ModularSearchProps,
  ViewSubGroupsPosition,
  ViewSubGroupsProps
} from '@ion/dls/src/types/types';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
import {ModularSearchWidget} from '@ion/features/src/search/ModularSearchWidget';
import {SearchFilters} from '@ion/features/src/search/types';
import {useCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
import {isEnabled as isPegWizardEnabled} from '@/routes/toolbox/pegwizard/store';
import Features from '@/security/Features';
import {ExportContext} from '../../types/redux';
import OnlyThisAccountSwitch from './devicefiles/OnlyThisAccountSwitch';

const MENU_ITEM_HEIGHT = 50;

// sizes the add menu to the config and peg wizard items actually shown...
const getMenuHeight = (hasConfig: boolean): string => (
  `${(Number(hasConfig) + Number(isPegWizardEnabled())) * MENU_ITEM_HEIGHT}px`
);

const getOverrides = (hasConfig: boolean) => () => ({
  paperSection : {
    width      : '163px',
    height     : getMenuHeight(hasConfig),
    display    : 'grid',
    marginLeft : 5,
    marginTop  : '-3px'
  }
});

const configOverrides   = getOverrides(true);
const noConfigOverrides = getOverrides(false);

// defines the component styles ...
export const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    adminContainer: provisioningContainer
  } = getConstrainedGridStyles(theme);
  return {
    provisioningContainer,
    provisioningPageHeader : {
      alignItems                     : 'center',
      display                        : 'flex',
      justifyContent                 : 'space-between',
      margin                         : '50px 0 0',
      [theme.breakpoints.down('sm')] : {
        margin : '0px'
      }
    },
    provisioningSearch : {
      maxWidth                       : '300px',
      width                          : '100%',
      [theme.breakpoints.down('sm')] : {
        display : 'none'
      }
    },
    provisioningIcons : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    mobileAddButton : {
      position : 'fixed',
      bottom   : '16px',
      right    : '18px',
      zIndex   : 1
    },
    iconButtonIcon : {
      color : dlsColor('white')
    },
    hideAddButton : {
      display : 'none'
    },
    alignRight : {
      justifyContent : 'flex-end'
    },
    subAccount : {
      paddingLeft : '35px'
    },
    onlyThisAccount : {
      marginRight : 'auto'
    },
    provisioningGrid : {
      display                        : 'flex',
      flexDirection                  : 'column',
      overflow                       : 'hidden',
      marginTop                      : '0px',
      marginBottom                   : '8px',
      position                       : 'relative',
      flex                           : 1,
      [theme.breakpoints.down('sm')] : {
        margin : '-26px 23px 40px 23px'
      }
    }
  };
});

// defines the names of each provisioning page which resolves to their respective
// navigation translation entries ...
export enum PageTitle {
  provisioningDevicefiles = 'provisioningDevicefiles',
  provisioningDownloadjobs = 'provisioningDownloadjobs',
  provisioningJobSearchEngine = 'provisioningJobSearchEngine'
};

export enum FilePermissions {
  DeviceFile = 'Device File',
  DownloadJob = 'Job'
}

// defines the provisioning container props ...
export type ProvisioningContainerProps = ViewSubGroupsProps &
ViewOnlyThisAccountProps &
ModularSearchProps &
{
  title: PageTitle,
  reducer: string,
  onExport: (params?: DownloadTemplate) => void,
  onRefresh: (refresh: boolean) => void,
  onShowColumnChooser: (show: boolean) => void,
  includeExport?: boolean,
  exportContext?: ExportContext,
  onAdd?: MouseEventHandler,
  onAddSecondary?: MouseEventHandler,
  grid: any,
  onUpdateGridColumns?: Function,
  searchLabel?: string,
  searchInstance?: BaseSearchMapping,
  getSearchParams?: Function,
  hasAddPermission?: boolean,
  hasAddFilePermissions?: boolean,
  entity?: string,
  isMobileModularSearch?: boolean,
  hasExportLimit?: boolean,
  getExportColumnDefinitions?: Function,
  totalRowCount?: number,
  search?: SearchFilters,
  excludeInheritable?: boolean,
  disableExport?: boolean,
  hasCreate?: boolean,
  isDeviceFiles?: boolean,
  hasNoRowSelection?: boolean
};

/**
 * The ProvisioningContainer provides the unified Provisioning Page wrapper which allows for a
 * consistent layout of page elements and features across iOn Provisioning Pages.
 *
 */
export const ProvisioningContainer = ({
  grid,
  title,
  preference,
  entity,
  reducer,
  onAdd,
  onAddSecondary,
  onExport,
  onRefresh,
  onShowColumnChooser,
  includeExport,
  onUpdateGridColumns,
  exportContext,
  searchLabel,
  getSearchParams,
  viewSubGroups,
  toggleViewSubGroups,
  totalRowCount,
  getExportColumnDefinitions,
  toggleViewOnlyThisAccount,
  viewOnlyThisAccount,
  search,
  isDeviceFiles = false,
  disableExport = false,
  searchInstance = null,
  hideSubGroups = false,
  hasCreate = false,
  isMobileModularSearch = false,
  hasAddPermission = false,
  hideOnlyThisAccount = false,
  autoSelectNext = false,
  hasExportLimit = false,
  excludeInheritable = false,
  isCtcAdmin = false,
  hasAddFilePermissions = false,
  hasNoRowSelection = false
}: ProvisioningContainerProps) => {
  const dispatch                                    = useDispatch();
  const classes                                     = useStyles();
  const {pending}                                   = useSelector(({exporter}) => exporter.current);
  const isExporting                                 = pending[exportContext] === true;
  const {isDesktop, isPhone}                        = useMedia();
  const user                                        = useCurrentUser().currentUser;
  const [autoSaveGridOption, setAutoSaveGridOption] = useState<boolean>(false);
  const [gridSaveInProgress, setGridSaveInProgress] = useState<boolean>(false);
  const hasChangeInGrid                             = useHasGridChanged(reducer);
  const gridMode                                    = hasChangeInGrid ? 'changed' : 'default';
  const gridState                                   = gridSaveInProgress ? 'inprogress' : gridMode;
  const userHasPermissionToAdd                      = !!(Features.calampFileAllowed || Features.customerFileAllowed);
  const path                                        = 'common:editors.filePermissions.info';
  const globalGroup                                 = useSelector(globalGroupSelector);
  const currentAccountId                            = parseInt(globalGroup?.href.split('/').reverse()[0], 10);
  const [emailExportSnackbarMessage,
    setEmailExportSnackbarMessage]                  = useState<string>('');

  const [isEmailExportRequest, setIsEmailExportRequest] = useState(false);

  useEffect(() => {
    setAutoSaveGridOption(isPreferenceAvailable('persistGridLayouts'));
  }, [user]);

  const toggleSubGroupsView = useCallback(() => {
    toggleViewSubGroups?.(!viewSubGroups);
  }, [viewSubGroups]);

  const toggleOnlyThisAccountView = useCallback(() => {
    toggleViewOnlyThisAccount?.(!viewOnlyThisAccount);
  }, [viewOnlyThisAccount]);

  const onExportCSV = useCallback(() => {
    if (hasExportLimit) {
      setIsEmailExportRequest(true);
    } else {
      onExport();
    }
  }, [hasExportLimit]);

  /**
   * @private
   */
  const updateGridSettings = useCallback(function() {
    if (hasChangeInGrid && onUpdateGridColumns) {
      setGridSaveInProgress(true);
      onUpdateGridColumns();
      setTimeout(() => {
        setGridSaveInProgress(false);
        dispatch(toggleUserPrompt({
          message          : translate('common:grids.success'),
          autoHideDuration : 3000
        }));
      }, 1000);
    }
  }, [hasChangeInGrid]);

  const onCloseEmailExportDialog = useCallback(() => {
    setIsEmailExportRequest(false);
  }, []);

  const handleSnackBar = useCallback(() => {
    setEmailExportSnackbarMessage('');
  }, []);

  return (
    <div className={classes.provisioningContainer}>
      <div className={classes.provisioningPageHeader}>
        {isDesktop && (
          <>
            <div>
              <HeaderTypography variant='h1'>
                {translate(`nav:side.${title}`)}
              </HeaderTypography>
            </div>
            <div className={classes.subAccount}>
              {!hideSubGroups && toggleViewSubGroups && (
                <div>
                  <ViewSubGroupsSwitch checked={viewSubGroups}
                    onChange={toggleSubGroupsView}
                    onChecked={toggleViewSubGroups}
                    preference={preference}
                    position={ViewSubGroupsPosition.GRID_CTAS}
                    labelColor={dlsColor('grey2')} />
                </div>
              )}
            </div>
            <div className={classes.onlyThisAccount}>
              {!hideOnlyThisAccount && toggleViewOnlyThisAccount && (
                <div>
                  <OnlyThisAccountSwitch checked={viewOnlyThisAccount}
                    onChange={toggleOnlyThisAccountView}
                    position={ViewSubGroupsPosition.GRID_CTAS} />
                </div>
              )}
            </div>
            <div className={classes.provisioningIcons}>
              <Render>
                <Render.If condition={isDeviceFiles}>
                  <PopupPrimaryButton onAddPrimary={onAdd}
                    onAddSecondary={onAddSecondary}
                    entity={entity}
                    tooltipTitle={path}
                    styleOverrides={hasCreate ? configOverrides : noConfigOverrides}
                    hideOption={classes.hideAddButton}
                    addEnabled={hasAddPermission}
                    primaryActionAllowed={hasAddFilePermissions}
                    secondaryAddAllowed={hasCreate}
                    secondaryActionAllowed={userHasPermissionToAdd} />
                </Render.If>
                <Render.Else>
                  <Tooltip arrow title={hasAddFilePermissions && translate(`${path}`, {entity: entity})}
                    disableHoverListener={!hasAddFilePermissions}>
                    <div>
                      <PrimaryButton className={classnames({
                        [classes.hideAddButton] : !userHasPermissionToAdd &&
                    !hasAddPermission
                      })}
                      disabled={hasAddFilePermissions}
                      startIcon={<Add />}
                      onClick={onAdd}>
                        <span>{translate('common:addNew')}</span>
                      </PrimaryButton>
                    </div>
                  </Tooltip>
                </Render.Else>
              </Render>
            </div>
          </>
        )}
        {isPhone && userHasPermissionToAdd && hasAddPermission && (
          <Tooltip arrow title={hasAddFilePermissions && translate(`${path}`, {entity: entity})}
            disableHoverListener={!hasAddFilePermissions}>
            <div className={classes.mobileAddButton}>
              <IconButton icon={<Add className={classes.iconButtonIcon} />}
                onClick={onAdd}
                disabled={hasAddFilePermissions}
                backgroundColor={dlsColor('grey1')} />
            </div>
          </Tooltip>
        )}
      </div>
      <div className={classes.provisioningPageHeader} style={{marginTop: !isPhone ? '24px' : '30px'}}>
        {searchInstance && (
          <ModularSearchWidget searchInstance={searchInstance}
            isPhone={isPhone}
            isMobileModularSearch={isMobileModularSearch}
            pageName={title}
            hasDateField={isCtcAdmin}
            inputPlaceholder={searchLabel ?? translate('common:modularsearch.inputplaceholder')}
            getSearchParams={getSearchParams}
            autoSelectNext={autoSelectNext} />
        )}
        <div className={classes.provisioningSearch} />
        {isDesktop && (
          <div className={classes.provisioningIcons}>
            <RefreshGridButton onRefresh={onRefresh} />
            {(onUpdateGridColumns && !autoSaveGridOption) && (
              <SaveIconButton type={gridState}
                onClick={updateGridSettings} />
            )}
            {includeExport && (
              <CSVExportButton onExport={onExportCSV}
                isExporting={isExporting}
                enableExport={disableExport}
                exportLimit={hasExportLimit}
                disableExport={!disableExport}
                hasNoRowSelection={hasNoRowSelection} />
            )}
            <ColumnChooserButton onShowColumnChooser={onShowColumnChooser} />
          </div>
        )}
        {isEmailExportRequest && (
          <EmailExportRequestDialog exportCount={totalRowCount}
            show={isEmailExportRequest}
            excludeInheritable={excludeInheritable}
            search={search}
            exportContext={ExportContext.deviceFiles}
            viewSubGroups={viewSubGroups}
            accountId={currentAccountId}
            searchFilters={grid?.props?.searchFilters}
            getExportColumnDefinitions={getExportColumnDefinitions}
            showSnackbar={setEmailExportSnackbarMessage}
            onClose={onCloseEmailExportDialog} />
        )}
        {emailExportSnackbarMessage && (
          <IonSnackbar show={!!emailExportSnackbarMessage}
            message={emailExportSnackbarMessage}
            actionLabel={translate('common:editors.delete.ok')}
            onAction={handleSnackBar}
            onClose={handleSnackBar}
            autoHideDuration={3000} />
        )}
      </div>
      <div className={classes.provisioningGrid}>
        {grid}
      </div>
    </div>
  );
};

export default ProvisioningContainer;
